import { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { AppShell, PageHeader } from '@/components/AppShell';
import { ScheduleSheet } from '@/components/ScheduleSheet';
import { RunAttributionBadge } from '@/components/vmos/RunAttributionBadge';
import { useUser } from '@/hooks/useUser';
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2, Play, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { fetchDevices, type VMOSDevice } from '@/services/vmos-api';
import { ActiveRunPanel } from '@/components/vmos/ActiveRunPanel';
import {
  listScheduledRuns,
  runNow,
  stopScheduledRun,
  type ScheduledRun,
} from '@/services/scheduled-runs-api';

function startOfWeek(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.getFullYear(), d.getMonth(), diff);
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function isToday(d: Date): boolean {
  const now = new Date();
  return (
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
}

function isInDay(iso: string | null | undefined, day: Date): boolean {
  if (!iso) return false;
  return isSameDay(new Date(iso), day);
}

function lastRunConfig(status?: string | null) {
  if (!status || status === 'completed' || status === 'running' || status === 'fired') return null;
  if (status === 'failed') {
    return { label: 'last run failed', cls: 'text-red-600 border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10' };
  }
  if (status === 'completed_with_issues') {
    return { label: 'last run issues', cls: 'text-amber-600 border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10' };
  }
  if (status === 'stopped') {
    return { label: 'last run stopped', cls: 'text-muted-foreground border-border bg-muted/40' };
  }
  return null;
}

function LastRunBadge({
  status,
  error,
  compact = false,
}: {
  status?: string | null;
  error?: string | null;
  compact?: boolean;
}) {
  const config = lastRunConfig(status);
  if (!config) return null;
  if (compact) {
    return (
      <span
        className={cn('inline-flex items-center shrink-0', config.cls.split(' ').find(c => c.startsWith('text-')) || 'text-red-600')}
        title={error ? `${config.label}: ${error}` : config.label}
      >
        <AlertTriangle className="h-3 w-3" />
      </span>
    );
  }
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium', config.cls)}
      title={error || undefined}
    >
      <AlertTriangle className="h-3 w-3" />
      {config.label}
    </span>
  );
}



export default function ScheduledRunsPage() {
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const meId = user?.id;
  const [ownerFilter, setOwnerFilter] = useState<'all' | 'mine'>('all');
  const [searchParams, setSearchParams] = useSearchParams();

  const viewMode = (searchParams.get('view') as 'daily' | 'weekly' | 'monthly') || 'weekly';
  const offset = parseInt(searchParams.get('offset') || '0', 10);

  const setViewModeAndParams = useCallback((mode: 'daily' | 'weekly' | 'monthly') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('view', mode);
      next.set('offset', '0');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setOffsetAndParams = useCallback((newOffset: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('view', viewMode);
      next.set('offset', String(newOffset));
      return next;
    }, { replace: true });
  }, [setSearchParams, viewMode]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ScheduledRun | null>(null);
  const [defaultSlotDate, setDefaultSlotDate] = useState<Date | undefined>(undefined);
  const [sheetReadOnly, setSheetReadOnly] = useState(false);

  const today = new Date();

  const days = useMemo(() => {
    switch (viewMode) {
      case 'daily': {
        return [addDays(today, offset)];
      }
      case 'weekly': {
        const weekStart = addDays(startOfWeek(addDays(today, offset * 7)), 0);
        return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
      }
      case 'monthly': {
        const monthStart = new Date(today.getFullYear(), today.getMonth() + offset, 1);
        const firstDayOfWeek = monthStart.getDay();
        const startDay = addDays(monthStart, -firstDayOfWeek);
        return Array.from({ length: 42 }, (_, i) => addDays(startDay, i));
      }
    }
  }, [viewMode, offset]);

  const visibleStart = formatDate(days[0]);
  const visibleEnd = formatDate(addDays(days[days.length - 1], 1));


  const { data: runs, isLoading } = useQuery({
    queryKey: ['scheduled-runs', visibleStart, visibleEnd],
    queryFn: () =>
      listScheduledRuns({
        start_date: visibleStart,
        end_date: visibleEnd,
      }),
    refetchInterval: 30000,
    staleTime: 10_000,         // 10s — no refetch on quick remount
    gcTime: 30 * 60 * 1000,    // 30 min — cache survives tab switches
    placeholderData: keepPreviousData,  // smooth date range transitions
  });

  const { data: devicesData } = useQuery({
    queryKey: ['vmos-devices'],
    queryFn: fetchDevices,
  });
  const devices = devicesData?.devices ?? [];

  const deviceMap = useMemo(() => {
    const map: Record<string, VMOSDevice> = {};
    for (const d of devices) {
      map[d.pad_id] = d;
    }
    return map;
  }, [devices]);

  const scopedRuns = useMemo(() => {
    const list = runs || [];
    return ownerFilter === 'mine' && meId
      ? list.filter((r: ScheduledRun) => r.user_id === meId)
      : list;
  }, [runs, ownerFilter, meId]);

  const activeRuns = scopedRuns.filter((r: ScheduledRun) => r.status === 'running');




  const getRunsForDay = (day: Date) => {
    if (!scopedRuns) return [];
    return scopedRuns.filter((r) => {
      if (r.status === 'running') {
        if (r.last_run_at && isInDay(r.last_run_at, day)) return true;
        return isToday(day);
      }
      if (r.status === 'skipped') {
        if (r.last_run_at && isInDay(r.last_run_at, day)) return true;
        return isToday(day);
      }
      if (r.next_run_at && isInDay(r.next_run_at, day)) return true;
      if (r.last_run_at && isInDay(r.last_run_at, day)) return true;
      return false;
    });
  };

  const handleSlotClick = (day: Date) => {
    setSheetReadOnly(false);
    setEditingSchedule(null);
    setDefaultSlotDate(day);
    setSheetOpen(true);
  };

  const handleBlockClick = useCallback((run: ScheduledRun) => {
    setSheetReadOnly(run.status === 'running');
    setEditingSchedule(run);
    setDefaultSlotDate(undefined);
    setSheetOpen(true);
  }, []);

  const handleRunNow = async (runId: string) => {
    try {
      await runNow(runId);
      toast.success('Run started');
      queryClient.invalidateQueries({ queryKey: ['scheduled-runs'] });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleStopRun = async (runId: string) => {
    if (!runId) {
      toast.error('No run to stop');
      return;
    }
    try {
      await stopScheduledRun(runId);
      toast.success('Run stopped');
      queryClient.invalidateQueries({ queryKey: ['scheduled-runs'] });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['scheduled-runs'] });
  };

  const periodLabel = useMemo(() => {
    switch (viewMode) {
      case 'daily':
        return days[0].toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
      case 'weekly': {
        const start = days[0];
        const end = days[6];
        if (start.getMonth() === end.getMonth()) {
          return `${start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} — ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${end.getDate()}`;
        }
        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
      case 'monthly':
        return days[15].toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  }, [viewMode, days]);

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        {/* Header */}
        <PageHeader className="justify-between border-input">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="[&_svg]:!hidden">
                <span className="sr-only">Toggle sidebar</span>
              </SidebarTrigger>
              <h1 className="text-lg font-bold tracking-tight">Schedule</h1>
              <span className="text-sm text-muted-foreground font-medium">{periodLabel}</span>
              <div className="flex bg-muted rounded-lg p-0.5 ml-2">
                <Button
                  variant={ownerFilter === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() => setOwnerFilter('all')}
                >
                  All
                </Button>
                <Button
                  variant={ownerFilter === 'mine' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() => setOwnerFilter('mine')}
                >
                  Mine
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={viewMode} onValueChange={(v) => { setViewModeAndParams(v as typeof viewMode); }}>
                <SelectTrigger className="w-28 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOffsetAndParams(0)}
              >
                Today
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOffsetAndParams(offset - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOffsetAndParams(offset + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button onClick={() => {
                setSheetReadOnly(false);
                setEditingSchedule(null);
                setDefaultSlotDate(new Date());
                setSheetOpen(true);
              }}>
                <Plus className="mr-2 h-4 w-4" />
                New Schedule
              </Button>
            </div>
        </PageHeader>

          {/* Calendar Grid */}
          <div className="flex-1 overflow-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : viewMode === 'monthly' ? (
              /* ── Monthly View ── */
              <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <div key={d} className="bg-card p-2 text-center border-b">
                    <span className="text-xs text-muted-foreground font-medium">{d}</span>
                  </div>
                ))}
                {days.map((day) => {
                  const dayRuns = getRunsForDay(day);
                  const inMonth = isSameMonth(day, days[15]);
                  return (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        'bg-card min-h-[100px] p-1.5 cursor-pointer',
                        isToday(day) && 'bg-accent',
                        !inMonth && 'opacity-40'
                      )}
                      onClick={() => handleSlotClick(day)}
                    >
                      <div className={cn(
                        'text-xs font-medium mb-0.5 w-6 h-6 flex items-center justify-center rounded-full',
                        isToday(day) && 'bg-primary text-primary-foreground'
                      )}>
                        {day.getDate()}
                      </div>
                      <div className="space-y-0.5">
                        {dayRuns.slice(0, 3).map((run) => (
                          <div
                            key={run.id}
                            className={cn(
                              'rounded-sm px-1 py-0.5 text-[10px] cursor-pointer truncate border',
                              run.status === 'skipped'
                                ? 'bg-red-50 text-red-800 border-red-300 dark:bg-red-500/15 dark:text-red-100 dark:border-red-500/30'
                                : run.status === 'running'
                                  ? 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-500/15 dark:text-amber-100 dark:border-amber-500/30'
                                  : run.run_type === 'content_upload'
                                    ? 'bg-violet-50 text-violet-800 border-violet-300 dark:bg-violet-500/15 dark:text-violet-100 dark:border-violet-500/30'
                                    : 'bg-lime-50 text-lime-800 border-lime-300 dark:bg-lime-500/15 dark:text-lime-100 dark:border-lime-500/30',
                              run.status === 'completed' && 'opacity-50'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBlockClick(run);
                            }}
                          >
                            <span className="inline-flex items-center gap-1 min-w-0">
                              <span className="truncate">{run.name}</span>
                              <LastRunBadge status={run.last_run_status} error={run.last_run_error} compact />
                            </span>
                          </div>
                        ))}
                        {dayRuns.length > 3 && (
                          <div className="text-[10px] text-muted-foreground pl-1">
                            +{dayRuns.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── Daily / Weekly View ── */
              <div className={cn(
                'grid gap-px bg-border rounded-lg overflow-hidden border',
                viewMode === 'daily' ? 'grid-cols-1' : 'grid-cols-7'
              )}>
                {/* Day headers */}
                {days.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      'bg-card p-3 text-center border-b',
                      isToday(day) && 'bg-primary/5'
                    )}
                  >
                    <div className="text-xs text-muted-foreground font-medium">
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div
                      className={cn(
                        'text-lg font-semibold mt-0.5',
                        isToday(day) && 'text-primary'
                      )}
                    >
                      {day.getDate()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {day.toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                  </div>
                ))}

                {/* Day columns */}
                {days.map((day) => {
                  const dayRuns = getRunsForDay(day);
                  return (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        'bg-card min-h-[200px] p-2 space-y-1.5 cursor-pointer',
                        isToday(day) && 'bg-primary/5'
                      )}
                      onClick={() => handleSlotClick(day)}
                    >
                      {dayRuns.map((run) => {
                        const isRunning = run.status === 'running';
                        const isSkipped = run.status === 'skipped';
                        const isContent = run.run_type === 'content_upload';
                        const padCount = run.config?.pad_ids?.length ?? 0;
                        const timeLabel = run.timing_mode === 'random'
                          ? 'random'
                          : formatTime(run.scheduled_at || run.next_run_at);

                        const block = (
                          <div
                            key={run.id}
                            className={cn(
                              'rounded-md px-2 py-1.5 text-xs cursor-pointer border transition-colors',
                              isRunning
                                ? 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 animate-pulse'
                                : isSkipped
                                  ? 'border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20'
                                  : isContent
                                    ? 'border-violet-300 bg-violet-50 dark:border-violet-500/40 dark:bg-violet-500/10 hover:bg-violet-100 dark:hover:bg-violet-500/20'
                                    : 'border-lime-300 bg-lime-50 dark:border-lime-500/40 dark:bg-lime-500/10 hover:bg-lime-100 dark:hover:bg-lime-500/20',
                              run.status === 'completed' && 'opacity-60'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBlockClick(run);
                            }}
                          >
                            <div className="font-medium truncate flex items-center gap-1">
                              {isRunning && (
                                <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                              )}
                              {isSkipped && (
                                <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                              )}
                              <span className="truncate">{run.name}</span>
                              {isSkipped && (
                                <button
                                  className="ml-auto p-0.5 rounded hover:bg-red-200 dark:hover:bg-red-500/30 flex-shrink-0"
                                  title="Run now"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRunNow(run.id);
                                  }}
                                >
                                  <Play className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            <div className="text-muted-foreground mt-0.5 flex justify-between">
                              <span>
                                {isContent ? 'Post' : 'Agent'}
                                {' · '}
                                {isSkipped ? 'skipped' : timeLabel}
                              </span>
                              <span className="flex items-center gap-1">
                                <LastRunBadge status={run.last_run_status} error={run.last_run_error} />
                                <RunAttributionBadge run={run} meId={meId} />
                                {padCount}d
                              </span>
                            </div>
                          </div>
                        );

                        if (isSkipped) {
                          return (
                            <Tooltip key={run.id}>
                              <TooltipTrigger asChild>
                                {block}
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[220px] text-xs">
                                This run was skipped due to a server restart. Edit to reschedule or delete.
                              </TooltipContent>
                            </Tooltip>
                          );
                        }

                        return block;
                      })}
                      {dayRuns.length === 0 && (
                        <div className="flex items-center justify-center h-full">
                          <span className="text-xs text-muted-foreground">
                            Click to add
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Live Panels */}
            {activeRuns.length > 0 && (
              <div className="border-t mt-4">
                {activeRuns.map((run: ScheduledRun) => (
                  <ActiveRunPanel
                    key={run.id}
                    run={run}
                    deviceMap={deviceMap}
                    onStop={() => handleStopRun(run.id)}
                    onViewDetails={() => {
                      setSheetReadOnly(true);
                      setEditingSchedule(run);
                      setSheetOpen(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

      <ScheduleSheet
        key={editingSchedule?.id ?? `_new_${defaultSlotDate?.getTime() ?? 0}`}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        schedule={editingSchedule}
        defaultDate={defaultSlotDate}
        readOnly={sheetReadOnly}
        onSaved={handleSaved}
      />
    </AppShell>
  );
}
